import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { RefreshIcon, CheckCircleIcon } from '../components/icons'; // Assuming CheckCircleIcon exists

// Type matching the notifications table structure
type Notification = {
  id: string;
  organization_id: string;
  user_id: string | null;
  type: string;
  message: string;
  related_entity_type: string | null;
  related_entity_id: string | null;
  is_read: boolean;
  created_at: string;
};

export function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { profile } = useAuth();
  const organizationId = profile?.organization_id;
  const userId = profile?.id;

  const fetchNotifications = useCallback(async () => {
    if (!organizationId || !userId) {
      setError('Organization or User context not found.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('notifications')
        .select('*')
        .eq('organization_id', organizationId)
        .or(`user_id.is.null,user_id.eq.${userId}`)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setNotifications(data || []);
    } catch (err: any) {
      console.error('Error fetching notifications:', err);
      setError(err.message || 'Failed to load notifications.');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, userId]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      const { error: updateError } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);
        // RLS policy should allow admins/superadmins to update is_read

      if (updateError) throw updateError;

      // Instead of optimistic update, refetch the list
      fetchNotifications(); // Call fetchNotifications to get the updated list
    } catch (err: any) {
       console.error('Error marking notification as read:', err);
       // Display error to user?
       setError(`Failed to mark as read: ${err.message}`);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Notifications
        </Typography>
        <Tooltip title="Refresh Notifications">
            {/* Wrap IconButton in a span to allow Tooltip when disabled */}
             <span>
               <IconButton onClick={fetchNotifications} disabled={loading}>
                 <RefreshIcon />
               </IconButton>
             </span>
        </Tooltip>
      </Box>

      {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '5%' }}>Status</TableCell>
                  <TableCell sx={{ width: '65%' }}>Message</TableCell>
                  <TableCell sx={{ width: '20%' }}>Date</TableCell>
                  <TableCell sx={{ width: '10%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No notifications found.
                    </TableCell>
                  </TableRow>
                ) : (
                  notifications.map((notification) => (
                    <TableRow 
                      key={notification.id} 
                      sx={{ backgroundColor: notification.is_read ? 'inherit' : 'action.hover' }}
                    >
                      <TableCell align="center">
                          <Chip 
                            label={notification.is_read ? 'Read' : 'Unread'}
                            size="small"
                            color={notification.is_read ? 'default' : 'primary'}
                          />
                      </TableCell>
                      <TableCell>{notification.message}</TableCell>
                      <TableCell>
                        {new Date(notification.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {!notification.is_read && (
                            <Tooltip title="Mark as Read">
                                <IconButton size="small" onClick={() => handleMarkAsRead(notification.id)}>
                                    {/* Placeholder for Check icon - replace CheckCircleIcon if needed */}
                                    <CheckCircleIcon fontSize="inherit" /> 
                                </IconButton>
                            </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
} 